import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { Bell, Check, CheckCheck, Radio, AlertTriangle, TrendingUp, Mail } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const SOURCE_META = {
  all:       { label: "Todos",      icon: Bell,           accent: "#0B1B2F" },
  djen:      { label: "DJEN",       icon: Radio,          accent: "#DC2626" },
  radar:     { label: "Radar",      icon: TrendingUp,     accent: "#B45309" },
  prazos:    { label: "Prazos",     icon: AlertTriangle,  accent: "#F59E0B" },
  cobrancas: { label: "Cobranças",  icon: Mail,           accent: "#D4AF37" },
};

const groupByDay = (items) => {
  const groups = {};
  for (const it of items) {
    const d = it.date ? new Date(it.date) : new Date();
    const key = d.toISOString().slice(0, 10);
    if (!groups[key]) groups[key] = [];
    groups[key].push(it);
  }
  const sorted = Object.keys(groups).sort().reverse();
  return sorted.map((k) => ({ dayKey: k, items: groups[k] }));
};

const formatDayHeader = (dayKey) => {
  const now = new Date();
  const target = new Date(dayKey + "T00:00:00");
  const diffDays = Math.round((now.setHours(0,0,0,0) - target.getTime()) / 86400000);
  if (diffDays === 0) return "Hoje";
  if (diffDays === 1) return "Ontem";
  return target.toLocaleDateString("pt-BR", { weekday: "long", day: "2-digit", month: "short" });
};

const formatTime = (iso) => {
  try {
    return new Date(iso).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
  } catch { return ""; }
};

export default function Notifications() {
  const [source, setSource] = useState("all");
  const [data, setData] = useState({ items: [], counts: {} });
  const [loading, setLoading] = useState(true);

  const load = async (src = source) => {
    setLoading(true);
    try {
      const r = await api.get(`/notifications/timeline?source=${src}&limit=200`);
      setData(r.data);
    } catch (e) {
      toast.error("Falha ao carregar notificações");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(source); /* eslint-disable-next-line */ }, [source]);

  const markRead = async (id) => {
    try {
      await api.post(`/notifications/${encodeURIComponent(id)}/read`);
      setData((d) => ({
        ...d,
        items: d.items.map((it) => it.id === id ? { ...it, read: true } : it),
      }));
    } catch {
      toast.error("Falha ao marcar como lida");
    }
  };

  const markAllRead = async () => {
    try {
      const r = await api.post(`/notifications/read-all?source=${source}`);
      toast.success(`${r.data.marked} notificação(ões) marcadas como lidas`);
      await load(source);
    } catch {
      toast.error("Falha ao marcar todas");
    }
  };

  const groups = useMemo(() => groupByDay(data.items || []), [data.items]);
  const counts = data.counts || {};
  const totalUnread = counts.total || 0;

  return (
    <div className="flex-1 min-w-0 flex flex-col p-6 lg:p-8 font-sans max-w-5xl mx-auto w-full" data-testid="notifications-page">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl inline-flex items-center justify-center"
               style={{ background: "linear-gradient(135deg, #0B1B2F 0%, #071B34 100%)" }}>
            <Bell className="w-5 h-5" style={{ color: "#F0D37A" }}/>
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">Central unificada</div>
            <h1 className="font-heading text-3xl font-semibold text-slate-900">Notificações</h1>
          </div>
        </div>
        <button data-testid="notif-mark-all-read" onClick={markAllRead}
          className="inline-flex items-center gap-2 h-10 px-4 rounded-xl bg-white border border-slate-200 hover:bg-slate-50 text-sm font-medium text-slate-700 transition shadow-sm">
          <CheckCheck className="w-4 h-4"/> Marcar todas como lidas
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2 mb-6" data-testid="notif-filters">
        {Object.entries(SOURCE_META).map(([key, meta]) => {
          const Icon = meta.icon;
          const isActive = source === key;
          const count = key === "all" ? totalUnread : (counts[key] || 0);
          return (
            <button key={key} data-testid={`notif-filter-${key}`}
              onClick={() => setSource(key)}
              className={`inline-flex items-center gap-2 h-9 px-3.5 rounded-full text-sm font-medium transition border ${
                isActive
                  ? "text-white shadow-md"
                  : "bg-white text-slate-700 border-slate-200 hover:border-slate-300"
              }`}
              style={isActive ? { background: meta.accent, borderColor: meta.accent } : undefined}>
              <Icon className="w-3.5 h-3.5"/>
              {meta.label}
              {count > 0 && (
                <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full min-w-[18px] text-center ${
                  isActive ? "bg-white/25 text-white" : "bg-slate-100 text-slate-700"
                }`}>
                  {count}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Timeline */}
      {loading ? (
        <div className="p-12 text-center text-sm text-slate-500" data-testid="notif-loading">Carregando…</div>
      ) : groups.length === 0 ? (
        <div className="rounded-2xl border border-slate-200 bg-white p-12 text-center" data-testid="notif-empty">
          <Bell className="w-10 h-10 text-slate-300 mx-auto mb-3"/>
          <div className="text-slate-600 font-medium">Sem notificações no momento</div>
          <div className="text-slate-400 text-sm mt-1">Volte mais tarde ou ajuste o filtro acima.</div>
        </div>
      ) : (
        <div className="space-y-8" data-testid="notif-timeline">
          {groups.map((g) => (
            <div key={g.dayKey}>
              <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold mb-3">
                {formatDayHeader(g.dayKey)}
              </div>
              <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden shadow-sm">
                <ul className="divide-y divide-slate-100">
                  {g.items.map((it) => (
                    <li key={it.id}
                        data-testid={`notif-item-${it.source}`}
                        className={`p-4 flex items-start gap-3 transition ${it.read ? "opacity-60" : "hover:bg-slate-50"}`}>
                      <span className="mt-1.5 w-2.5 h-2.5 rounded-full shrink-0"
                            style={{ background: it.accent || "#0B1B2F" }}/>
                      <Link to={it.url || "#"} className="flex-1 min-w-0 block">
                        <div className="flex items-baseline justify-between gap-3">
                          <div className="font-medium text-slate-900 text-sm truncate">{it.title}</div>
                          <div className="text-[11px] text-slate-400 shrink-0">{formatTime(it.date)}</div>
                        </div>
                        {it.subtitle && (
                          <div className="text-xs text-slate-500 mt-1 line-clamp-2">{it.subtitle}</div>
                        )}
                      </Link>
                      {!it.read && (
                        <button data-testid={`notif-mark-${it.source}`}
                          onClick={(e) => { e.preventDefault(); markRead(it.id); }}
                          className="shrink-0 inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-900 transition px-2 py-1 rounded-md hover:bg-slate-100"
                          title="Marcar como lida">
                          <Check className="w-3.5 h-3.5"/>
                        </button>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
